import {
  React,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);



import {
  Box,
  FormControl,
  FormLabel,
  Radio,
  Checkbox,
  RadioGroup,
  Card,
  CardContent,
  CardHeader,
  colors,
  Divider,
  FormControlLabel,
  useTheme
} from '@mui/material';
import LoadingOverlay from 'react-loading-overlay-nextgen';
import { transpose } from 'src/utils/miscTools';
import InfoModal from 'src/components/InfoModal';
import InfoPopupContent from 'src/__mocks__/InfoPopupContent';

import {days_performance_url} from 'src/utils/constants';

function getData(token, deviceId, date, range, trackingOnly, setDaysincident, setDayscollected, setDaysreadingtimes, cardCallback, setIsLoading) {
  setIsLoading(true);
  const postBody = JSON.stringify({
    DATE: date,
    CUMULATIVE: true,
    DISH_IDS: deviceId,
    RANGE: range,
    TRACKING_ONLY: trackingOnly
  });
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: 'Token ' + token
  }
  fetch(days_performance_url, {
    method: 'POST',
    headers: headers,
    body: postBody
  }).then((response) => {
    if (response.ok) return response.json();
    throw response;
  }).then((json) => {
    if (json.length > 0) {
      const x = transpose(json);
      setDaysreadingtimes(x[0]);
      setDaysincident(x[1]);
      setDayscollected(x[2]);

      const totalDNI = x[1][x[1].length - 1];
      const totalCollected = x[2][x[2].length - 1];
      const lastReading = x[0][x[0].length - 1];
      setIsLoading(false);
      
      cardCallback(totalDNI, totalCollected, lastReading, x[1], x[2]);
    } else {
      setIsLoading(false);
      setDaysreadingtimes([]);
      setDaysincident([]);
      setDayscollected([]);
    }
  }).catch((err) => {
    console.error(err);
  });
}

const CumulativePerformance = (props) => {
  const {
    date,
    deviceId,
    cardCallback,
    token
  } = props;

  const [range, setRange] = useState('Peak Hours');
  const [trackingDataOnly, setTrackingDataOnly] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [daysincident, setDaysincident] = useState([]);
  const [dayscollected, setDayscollected] = useState([]);
  const [daysreadingtimes, setDaysreadingtimes] = useState([]);

  function onRangeChange(selectedRange) {
    setRange(selectedRange);
    getData(token, deviceId, date, selectedRange, trackingDataOnly, setDaysincident, setDayscollected, setDaysreadingtimes, cardCallback, setIsLoading);
  }

  function onTrackingOnlyChange() {
    const changedValue = !trackingDataOnly;
    setTrackingDataOnly(changedValue);
    getData(token, deviceId, date, range, changedValue, setDaysincident, setDayscollected, setDaysreadingtimes, cardCallback, setIsLoading);
  }

  useEffect(() => {
    if (deviceId.length > 0) {
      getData(token, deviceId, date, range,trackingDataOnly, setDaysincident, setDayscollected, setDaysreadingtimes, cardCallback, setIsLoading);
    }
  }, [date, deviceId]);

  const theme = useTheme();

  const times = [];

  for (const dateString of daysreadingtimes) {
    const timePart = dateString.split('T')[1];
    times.push(timePart);
  }

  const incidentEnergyDataset = {
    labels: times,
    label: 'Incident Energy',
    pointRadius: 0,
    borderColor: colors.blue[500],
    borderWidth: 1,
    data: daysincident.map((x) => Number(x).toFixed(2))
  };

  const enthalpyChangeDataset = {
    labels: times,
    label: 'Energy Collected',
    pointRadius: 0,
    borderColor: colors.red[500],
    borderWidth: 1,
    data: dayscollected.map((x) => Number(x).toFixed(2))
  };

  const data = {
    datasets: [
      incidentEnergyDataset,
      enthalpyChangeDataset
    ],
    labels: times
  };

  const options = {
    responsive: true,
    layout: { padding: 0 },
    maintainAspectRatio: false,
    
    plugins: {
      legend: { display: false },
    }
  };
  const titleText = "Day's Performance Cumulative";
  const unit = "(Kilowatt-Hours)"
  const title = (
    <>
      {titleText}<br/>{unit}
      <InfoModal content={InfoPopupContent.PERFORMANCE_CUMULATIVE} />
    </>
  );

  return (
    <Card>
      <CardHeader
        action={(
          <div>
            <FormControl component="fieldset">
              <FormControlLabel  
                control={
                  <Checkbox 
                    defaultChecked 
                    onChange={onTrackingOnlyChange}
                  />
                } 
                label="Tracking Data Only" 
              />
            </FormControl>
            <br/>
            <FormControl component="fieldset">
              <FormLabel component="legend">Range</FormLabel>
              <RadioGroup
                row
                aria-label="range"
                name="row-radio-buttons-group"
                onChange={(e) => onRangeChange(e.target.value)}
              >
                <FormControlLabel value="Morn-Eve" control={<Radio checked={range === 'Morn-Eve'} />} label="Morn-Eve" />
                <FormControlLabel value="Peak Hours" control={<Radio checked={range === 'Peak Hours'} />} label="Peak Hours" />
                <FormControlLabel value="24 Hours" control={<Radio checked={range === '24 Hours'} />} label="24 Hours" />
              </RadioGroup>
            </FormControl>
          </div>
        )}
        title={title}
      />
      <Divider />
      <CardContent>
        <Box
          sx={{
            height: 500,
            position: 'relative'
          }}
        >
          <LoadingOverlay
            active={isLoading}
            spinner
            text="Loading Data..."
            styles={{
              wrapper: (base) => ({
                ...base,
                height: 500,
              })
            }}
          >
            <Line
              data={data}
              options={options}
            />
          </LoadingOverlay>
        </Box>
      </CardContent>
      <Divider />
    </Card>
  );
};

CumulativePerformance.propTypes = {
  date: PropTypes.object,
  cardCallback: PropTypes.func,
  token: PropTypes.string,
  deviceId: PropTypes.array
};

export default CumulativePerformance;
